import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  createAgeValidation,
  updateAgeValidation,
} from "../../utils/ApiCalls/AgeValidation";
import { addlog } from "../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";

const FIELD_LABELS = { name: "Name", code: "Code", age: "Age" };

// Builds "Age: 18 -> 21, Code: OLD -> NEW" from only the fields that actually changed
const buildFieldChanges = (oldValues, newValues) =>
  Object.keys(FIELD_LABELS)
    .filter((key) => String(oldValues[key]) !== String(newValues[key]))
    .map((key) => `${FIELD_LABELS[key]}: ${oldValues[key]} -> ${newValues[key]}`)
    .join(", ");

const AgeValidationInput = ({ ageValidation, readOnly }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [age, setAge] = useState("");
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [error, setError] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    if (ageValidation?.id) {
      setName(ageValidation.name || "");
      setCode(ageValidation.code || "");
      setAge(ageValidation.age ?? "");
    }
  }, [ageValidation]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!name || !code || age === "") {
      const message = "Error: The following fields are empty - name, code, age";
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message, type: "error" },
      });
      setError(message);
      return;
    }

    setError();

    const values = { name, code, age: Number(age) };

    const data = ageValidation?.id
      ? await updateAgeValidation(ageValidation.id, values)
      : await createAgeValidation(values);

    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }

    let action = LOG_ACTIONS.CREATED;
    if (ageValidation?.id) {
      const changes = buildFieldChanges(
        { name: ageValidation.name, code: ageValidation.code, age: ageValidation.age },
        values
      );
      action = changes ? `${LOG_ACTIONS.UPDATED} (${changes})` : LOG_ACTIONS.UPDATED;
    }

    addlog(
      action,
      LOG_TYPES.AGE_VALIDATION,
      { id: ageValidation?.id || data?.data?.id, title: name },
      userDetails.id
    );

    navigate("/shore/mobile-masters/age-validation");
    dispatch({
      type: "SET_ERROR_MESSAGE",
      errMess: {
        message: ageValidation?.id
          ? "Age validation updated"
          : "Age validation created",
        type: "success",
      },
    });
  };

  return (
    <div>
      <section className="content">
        {error && (
          <div className="d-flex justify-content-center align-items-center text-danger">
            <p>{error}</p>
          </div>
        )}

        <div className="container-fluid">
          <div className="card" style={readOnly ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
            <form className="input_form" onSubmit={submitHandler}>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="name" className="f_label">Name</label>
                      <input
                        id="name"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="code" className="f_label">Code</label>
                      <input
                        id="code"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="age" className="f_label">Age</label>
                      <input
                        id="age"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="number"
                        min={0}
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!readOnly && (
                <div className="card-footer text-right">
                  <button className="btn main_submit">
                    {ageValidation?.id ? "Update" : "Add"} age validation
                  </button>
                </div>
              )}
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default AgeValidationInput;
